import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useDebounce } from 'uikit';

import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { catalogIsUniqueConstructionTypeCodeGql } from '../catalogConstructionTypesQueries';
import { CODE_PATTERN, trimValue } from '../helpers';

const requestId = 'isUniqueConstructionTypeCode';

interface IUseConstructionTypeCodeUniqueness {
    code: string | null;
    isEnabled: boolean;
}

export const useConstructionTypeCodeUniqueness = ({ code, isEnabled }: IUseConstructionTypeCodeUniqueness) => {
    const dispatch = useDispatch();
    const [checkedCode, setCheckedCode] = useState<string | null>(null);

    const [resetUniqueData] = useResetStoreData({ requestId });

    const [isUniqueData, isUniqueStatus, , isUniqueRequest] = useRequest({
        requestId,
        query: catalogIsUniqueConstructionTypeCodeGql,
        withEffect: false,
    });

    useEffect(() => {
        resetUniqueData(undefined);
    }, [resetUniqueData]);

    const trimmedCode = trimValue(code);
    const isValidCode = CODE_PATTERN.test(trimmedCode);

    const debouncedCheck = useDebounce(() => {
        if (!isEnabled || !isValidCode || trimmedCode === checkedCode) {
            return;
        }

        dispatch(isUniqueRequest({ code: trimmedCode }));
        setCheckedCode(trimmedCode);
    }, 500);

    useEffect(() => {
        debouncedCheck();
    }, [debouncedCheck, trimmedCode]);

    const isPending = trimmedCode !== checkedCode || isUniqueStatus === RequestStatus.FETCHING;
    const checkResult = isUniqueData?.catalogIsUniqueConstructionTypeCode?.result;

    const isCodeNotUnique = isEnabled && isValidCode && !isPending && checkResult === false;
    const isCodeUnique = !isEnabled || (isValidCode && !isPending && checkResult === true);

    return { isCodeNotUnique, isCodeUnique };
};
